import { Link } from "react-router";
import { useTheme } from "@exiftrator/shared/contexts";
import { ThemeToggle } from "@exiftrator/shared/components";
import { usePageTitle } from "@exiftrator/shared/hooks";
import { formatPageTitle } from "@exiftrator/shared/utils";
import logoLight from "@exiftrator/shared/assets/logo_light.svg";
import logoDark from "@exiftrator/shared/assets/logo_dark.svg";

const CONTAINER_CLASS = "mx-auto w-full max-w-[1440px] px-6 sm:px-8 lg:px-12";

export default function PrivacyPolicyPage() {
  usePageTitle(formatPageTitle("Privacy Policy"));

  const { theme } = useTheme();

  return (
    <div className="min-h-screen bg-bg-deep text-text-primary">
      <header className="sticky top-0 z-50 border-b border-border-default/70 bg-bg-deep/85 backdrop-blur-xl">
        <div className={`${CONTAINER_CLASS} flex items-center justify-between py-4`}>
          <Link to="/" className="shrink-0">
            <img src={theme === "dark" ? logoDark : logoLight} alt="Exiftrator" className="h-9 w-auto" />
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <main className={`${CONTAINER_CLASS} py-16`}>
        <article className="mx-auto max-w-3xl">
          <h1 className="text-3xl font-bold tracking-tight text-text-primary sm:text-4xl">Privacy Policy</h1>
          <p className="mt-2 text-sm text-text-supporting">Last updated: February 15, 2026</p>

          <div className="mt-10 space-y-8 text-base leading-relaxed text-text-secondary">
            <section>
              <h2 className="mb-3 text-xl font-semibold text-text-primary">Summary</h2>
              <p>
                Exiftrator is a local-first image metadata editor. Your photos and metadata are processed
                entirely in your browser. We do not upload, store, or have access to your files.
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-semibold text-text-primary">Data We Do Not Collect</h2>
              <ul className="list-disc space-y-1.5 pl-5">
                <li>Your photos or image files</li>
                <li>EXIF metadata contained in your photos</li>
                <li>GPS coordinates or location data from your images</li>
                <li>File names, thumbnails, or any image content</li>
              </ul>
              <p className="mt-3">
                All image processing — reading, editing, and writing metadata — happens locally in your
                browser using client-side JavaScript. No image data is transmitted to any server.
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-semibold text-text-primary">Data We May Collect</h2>
              <p>When you visit exiftrator.com, we may collect:</p>
              <ul className="mt-2 list-disc space-y-1.5 pl-5">
                <li>
                  <strong className="text-text-primary">Basic analytics:</strong> page views, referral source, browser type,
                  and device type. This data is anonymized and contains no personal identifiers.
                </li>
                <li>
                  <strong className="text-text-primary">Local storage:</strong> your theme preference (light/dark mode)
                  and app settings are stored in your browser's localStorage. This data never leaves your device.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-semibold text-text-primary">Third-Party Services</h2>
              <ul className="list-disc space-y-1.5 pl-5">
                <li>
                  <strong className="text-text-primary">Google Fonts:</strong> we load the Instrument Sans and JetBrains Mono
                  typefaces from Google Fonts. Google may collect standard web request data (IP address, user agent) when
                  serving these fonts. See{" "}
                  <a href="https://policies.google.com/privacy" className="underline hover:text-text-primary" target="_blank" rel="noopener noreferrer">
                    Google's Privacy Policy
                  </a>.
                </li>
                <li>
                  <strong className="text-text-primary">OpenStreetMap / Nominatim:</strong> if you use the GPS location search
                  feature, search queries are sent to the Nominatim geocoding service. See{" "}
                  <a href="https://osmfoundation.org/wiki/Privacy_Policy" className="underline hover:text-text-primary" target="_blank" rel="noopener noreferrer">
                    OSM Foundation's Privacy Policy
                  </a>.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-semibold text-text-primary">Cookies</h2>
              <p>
                Exiftrator does not use cookies. Theme preferences and application settings are stored
                in localStorage, which is not shared with any server.
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-semibold text-text-primary">Desktop App (Electron)</h2>
              <p>
                The Exiftrator desktop app processes all files locally on your device. It does not
                transmit any file data, metadata, or usage telemetry to external servers. Settings
                are stored locally on your filesystem.
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-semibold text-text-primary">Children's Privacy</h2>
              <p>
                Exiftrator is not directed at children under 13 and does not knowingly collect personal
                information from children.
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-semibold text-text-primary">Changes to This Policy</h2>
              <p>
                We may update this privacy policy from time to time. Changes will be posted on this
                page with an updated "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-semibold text-text-primary">Contact</h2>
              <p>
                If you have questions about this privacy policy, please open an issue on our GitHub repository.
              </p>
            </section>
          </div>
        </article>
      </main>

      <footer className="border-t border-border-default bg-bg-deep">
        <div className={`${CONTAINER_CLASS} flex flex-col items-center justify-between gap-2 py-6 text-xs text-text-supporting sm:flex-row`}>
          <p>© {new Date().getFullYear()} Exiftrator</p>
          <Link to="/" className="transition-colors hover:text-text-primary">Back to home</Link>
        </div>
      </footer>
    </div>
  );
}
